import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { MonthSelect } from "./FilterBar";
import { monthShift, useFilters } from "../store";

const int = (v: number) => Math.round(v).toLocaleString();
const yen = (v: number) => `${Math.round(v).toLocaleString()}円`;
const days = (v: number | null) => (v == null ? "—" : `${v.toFixed(1)}日`);
const shortName = (v: string) => v.replace(/^短手３（|）$/g, "");
const SHORT_STAY3_START = "2026-06";
const ROWS_PER_PAGE = 10;
const WEEKDAYS = ["月", "火", "水", "木", "金", "土", "日"];
type SortKey =
  | "kcode"
  | "itemName"
  | "points"
  | "revenuePerCase"
  | "cases"
  | "inpatientRevenue"
  | "dailyFee"
  | "q1"
  | "median"
  | "q3"
  | "preOpMedian"
  | "postOpMedian";

type Hist = NonNullable<
  ReturnType<typeof useQuery> extends never
    ? never
    : Awaited<ReturnType<typeof api.shortStay3Analysis>>
>["histogram"][number];

export function ShortStay3Sidebar({ dateMax }: { dateMax: string }) {
  const f = useFilters();
  const max = dateMax.slice(0, 7);
  const from = f.periodFrom.slice(0, 7);
  const to = f.periodTo.slice(0, 7);
  const navButton =
    "w-7 h-7 shrink-0 border border-slate-300 rounded bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed";
  const setFrom = (ym: string) => {
    f.setPeriodFrom(`${ym}-01`);
    if (ym > to) f.setPeriodTo(`${ym}-01`);
  };
  const setTo = (ym: string) => {
    f.setPeriodTo(`${ym}-01`);
    if (ym < from) f.setPeriodFrom(`${ym}-01`);
  };
  const monthControl = (
    label: string,
    value: string,
    onChange: (ym: string) => void,
  ) => {
    const previous = monthShift(`${value}-01`, -1).slice(0, 7);
    const next = monthShift(`${value}-01`, 1).slice(0, 7);
    return (
      <div className="space-y-1.5 text-xs">
        <div className="font-semibold text-slate-600">{label}</div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className={navButton}
            disabled={previous < SHORT_STAY3_START}
            onClick={() => onChange(previous)}
            title="前月"
          >
            ←
          </button>
          <MonthSelect
            value={value}
            min={SHORT_STAY3_START}
            max={max}
            onChange={onChange}
            className="min-w-0 flex-1 border border-slate-300 rounded px-2 py-1.5 bg-white"
          />
          <button
            type="button"
            className={navButton}
            disabled={next > max}
            onClick={() => onChange(next)}
            title="翌月"
          >
            →
          </button>
        </div>
      </div>
    );
  };
  return (
    <aside className="card h-fit p-3 space-y-4">
      <div className="text-sm font-semibold text-slate-800">短期滞在3分析</div>
      <section className="border-t border-slate-200 pt-3 space-y-4">
        {monthControl("退院月・開始", from, setFrom)}
        {monthControl("退院月・終了", to, setTo)}
      </section>
    </aside>
  );
}

export function ShortStay3AnalysisPanel() {
  const f = useFilters();
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<{ key: SortKey; direction: "asc" | "desc" }>(
    {
      key: "inpatientRevenue",
      direction: "desc",
    },
  );
  const q = useQuery({
    queryKey: ["short_stay3_analysis", f.periodFrom, f.periodTo],
    queryFn: () =>
      api.shortStay3Analysis({ dateFrom: f.periodFrom, dateTo: f.periodTo }),
  });
  const items = useMemo(
    () => aggregateItems(q.data?.histogram ?? []),
    [q.data],
  );
  const pointsByAccountingCode = useMemo(
    () =>
      new Map(
        (q.data?.definitions ?? []).map((r) => [r.accountingCode, r.points]),
      ),
    [q.data],
  );
  const activeCode =
    selectedCode && items.some((r) => r.accountingCode === selectedCode)
      ? selectedCode
      : (items[0]?.accountingCode ?? null);
  const selected = items.find((r) => r.accountingCode === activeCode);
  const selectedRows = (q.data?.histogram ?? []).filter(
    (r) =>
      r.accountingCode === activeCode && r.completed && r.lengthOfStay != null,
  );
  const pageCount = Math.max(1, Math.ceil(items.length / ROWS_PER_PAGE));
  const sortedItems = useMemo(() => {
    const value = (
      r: (typeof items)[number],
      key: SortKey,
    ): string | number | null => {
      switch (key) {
        case "kcode":
          return r.kcode ?? "";
        case "itemName":
          return r.itemName;
        case "points":
          return pointsByAccountingCode.get(r.accountingCode) ?? null;
        case "revenuePerCase":
          return r.cases ? r.inpatientRevenue / r.cases : null;
        case "cases":
          return r.cases;
        case "inpatientRevenue":
          return r.inpatientRevenue;
        case "dailyFee":
          return r.totalDays ? r.inpatientRevenue / r.totalDays : null;
        case "q1":
          return r.q1;
        case "median":
          return r.median;
        case "q3":
          return r.q3;
        case "preOpMedian":
          return r.preOpMedian;
        case "postOpMedian":
          return r.postOpMedian;
      }
    };
    return [...items].sort((a, b) => {
      const av = value(a, sort.key);
      const bv = value(b, sort.key);
      if (av == null) return 1;
      if (bv == null) return -1;
      const compared =
        typeof av === "string" && typeof bv === "string"
          ? av.localeCompare(bv, "ja")
          : Number(av) - Number(bv);
      return sort.direction === "asc" ? compared : -compared;
    });
  }, [items, pointsByAccountingCode, sort]);
  const pageItems = sortedItems.slice(
    page * ROWS_PER_PAGE,
    (page + 1) * ROWS_PER_PAGE,
  );
  useEffect(() => setPage(0), [f.periodFrom, f.periodTo]);
  useEffect(() => {
    if (page >= pageCount) setPage(pageCount - 1);
  }, [page, pageCount]);
  const toggleSort = (key: SortKey) => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" },
    );
  };
  const sortHeader = (
    label: React.ReactNode,
    key: SortKey,
    className: string,
    rowSpan?: number,
  ) => (
    <th className={className} rowSpan={rowSpan}>
      <button
        type="button"
        className="inline-flex items-center gap-1 hover:text-brand"
        onClick={() => toggleSort(key)}
      >
        {label}
        <span className="text-[9px] text-slate-400">
          {sort.key === key ? (sort.direction === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </th>
  );

  if (q.isLoading)
    return (
      <div className="card p-6 text-sm text-slate-500">
        短期滞在3分析を読み込み中…
      </div>
    );
  if (q.error || !q.data)
    return (
      <div className="card p-6 text-sm text-red-700">
        短期滞在3分析を取得できません: {(q.error as Error)?.message}
      </div>
    );

  return (
    <div className="short-stay3-analysis space-y-3">
      <section className="short-stay3-summary card p-4">
        <h3 className="font-semibold">短期滞在手術等基本料３　集計表</h3>
        <table className="w-full text-xs mt-4 min-w-[1280px]">
          <thead>
            <tr className="border-b bg-slate-50">
              {sortHeader("Kコード", "kcode", "text-left p-2 align-middle", 2)}
              {sortHeader(
                "算定項目",
                "itemName",
                "text-left p-2 align-middle",
                2,
              )}
              {sortHeader("点数", "points", "text-right p-2 align-middle", 2)}
              {sortHeader(
                "入院収入/件",
                "revenuePerCase",
                "text-right p-2 align-middle",
                2,
              )}
              {sortHeader("件数", "cases", "text-right p-2 align-middle", 2)}
              {sortHeader(
                "入院収入合計",
                "inpatientRevenue",
                "text-right p-2 align-middle",
                2,
              )}
              {sortHeader(
                "1日入院単価",
                "dailyFee",
                "text-right p-2 align-middle",
                2,
              )}
              <th
                colSpan={3}
                className="text-center p-2 border-l border-slate-200"
              >
                在院日数
              </th>
              {sortHeader(
                <>
                  <>術前日数</>
                  <br />
                  中央値
                </>,
                "preOpMedian",
                "text-right p-2 align-middle border-l border-slate-200",
                2,
              )}
              {sortHeader(
                <>
                  <>術後日数</>
                  <br />
                  中央値
                </>,
                "postOpMedian",
                "text-right p-2 align-middle",
                2,
              )}
            </tr>
            <tr className="border-b bg-slate-50">
              {sortHeader(
                "P25",
                "q1",
                "text-right p-2 border-l border-slate-200",
              )}
              {sortHeader("中央値", "median", "text-right p-2")}
              {sortHeader("P75", "q3", "text-right p-2")}
            </tr>
          </thead>
          <tbody>
            {pageItems.map((r) => (
              <tr
                key={`${r.kcode}-${r.accountingCode}`}
                onClick={() => setSelectedCode(r.accountingCode)}
                className={`border-b cursor-pointer transition ${r.accountingCode === activeCode ? "bg-amber-100/70" : "border-slate-100 hover:bg-amber-50"}`}
              >
                <td className="p-2 font-mono">{r.kcode ?? "—"}</td>
                <td className="p-2 font-medium">{shortName(r.itemName)}</td>
                <td className="p-2 text-right">
                  {pointsByAccountingCode.has(r.accountingCode)
                    ? `${int(pointsByAccountingCode.get(r.accountingCode)!)}点`
                    : "—"}
                </td>
                <td className="p-2 text-right">
                  {r.cases ? yen(r.inpatientRevenue / r.cases) : "—"}
                </td>
                <td className="p-2 text-right">{int(r.cases)}</td>
                <td className="p-2 text-right">{yen(r.inpatientRevenue)}</td>
                <td className="p-2 text-right font-semibold">
                  {r.totalDays
                    ? `${yen(r.inpatientRevenue / r.totalDays)}/日`
                    : "—"}
                </td>
                <td className="p-2 text-right border-l border-slate-200">
                  {days(r.q1)}
                </td>
                <td className="p-2 text-right font-semibold">
                  {days(r.median)}
                </td>
                <td className="p-2 text-right">{days(r.q3)}</td>
                <td className="p-2 text-right border-l border-slate-200">
                  {days(r.preOpMedian)}
                </td>
                <td className="p-2 text-right">{days(r.postOpMedian)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-end gap-2 mt-3 text-xs">
          <span className="text-slate-500">
            {items.length}件中 {items.length ? page * ROWS_PER_PAGE + 1 : 0}–
            {Math.min((page + 1) * ROWS_PER_PAGE, items.length)}件
          </span>
          <button
            type="button"
            className="px-2 py-1 border border-slate-300 rounded disabled:opacity-40"
            disabled={page === 0}
            onClick={() => setPage((v) => v - 1)}
          >
            ← 前へ
          </button>
          <span>
            {page + 1} / {pageCount}
          </span>
          <button
            type="button"
            className="px-2 py-1 border border-slate-300 rounded disabled:opacity-40"
            disabled={page >= pageCount - 1}
            onClick={() => setPage((v) => v + 1)}
          >
            次へ →
          </button>
        </div>
      </section>
      {selected && <ShortStayDetail item={selected} rows={selectedRows} />}
    </div>
  );
}

function aggregateItems(rows: Hist[]) {
  const map = new Map<
    string,
    {
      accountingCode: string;
      kcode: string | null;
      itemName: string;
      cases: number;
      inpatientRevenue: number;
      completed: number;
      totalDays: number;
      values: number[];
      preOpValues: number[];
      postOpValues: number[];
    }
  >();
  for (const r of rows) {
    const key = `${r.kcode ?? "—"}|${r.accountingCode}`;
    const x = map.get(key) ?? {
      accountingCode: r.accountingCode,
      kcode: r.kcode,
      itemName: r.itemName,
      cases: 0,
      inpatientRevenue: 0,
      completed: 0,
      totalDays: 0,
      values: [],
      preOpValues: [],
      postOpValues: [],
    };
    x.cases += r.cases;
    x.inpatientRevenue += r.inpatientRevenue;
    if (r.completed && r.lengthOfStay != null) {
      x.completed += r.cases;
      x.totalDays += r.lengthOfStay * r.cases;
      x.values.push(...Array(r.cases).fill(r.lengthOfStay));
    }
    if (r.preOpDays != null)
      x.preOpValues.push(...Array(r.cases).fill(r.preOpDays));
    if (r.postOpDischargeDays != null)
      x.postOpValues.push(...Array(r.cases).fill(r.postOpDischargeDays));
    map.set(key, x);
  }
  return [...map.values()]
    .map((x) => ({
      ...x,
      q1: quantile(x.values, 0.25),
      median: quantile(x.values, 0.5),
      q3: quantile(x.values, 0.75),
      preOpMedian: quantile(x.preOpValues, 0.5),
      postOpMedian: quantile(x.postOpValues, 0.5),
    }))
    .sort((a, b) => b.inpatientRevenue - a.inpatientRevenue);
}
function quantile(values: number[], q: number) {
  if (!values.length) return null;
  const a = [...values].sort((x, y) => x - y);
  const p = (a.length - 1) * q;
  const lo = Math.floor(p);
  const hi = Math.ceil(p);
  return a[lo] + (a[hi] - a[lo]) * (p - lo);
}
function histogram(
  rows: Hist[],
  field: "lengthOfStay" | "preOpDays" | "postOpDischargeDays",
) {
  const filtered = rows.filter((r) => r[field] != null);
  const max = Math.max(5, ...filtered.map((r) => r[field] ?? 0));
  const bins = Array.from({ length: Math.min(max + 1, 11) }, (_, i) => ({
    day: i,
    cases: 0,
  }));
  for (const r of filtered) bins[Math.min(r[field] ?? 0, 10)].cases += r.cases;
  return bins;
}
function ShortStayDetail({
  item,
  rows,
}: {
  item: ReturnType<typeof aggregateItems>[number];
  rows: Hist[];
}) {
  const main = histogram(rows, "lengthOfStay");
  const pre = histogram(rows, "preOpDays");
  const post = histogram(rows, "postOpDischargeDays");
  const histogramMax = Math.max(
    1,
    ...main.map((x) => x.cases),
    ...pre.map((x) => x.cases),
    ...post.map((x) => x.cases),
  );
  const simulation = [1, 2, 3, 4, 5].map((days) => ({
    days,
    unit: item.cases ? item.inpatientRevenue / item.cases / days : 0,
    current: days === item.median,
  }));
  return (
    <section className="short-stay3-detail card p-4">
      <div className="flex justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] text-amber-700 font-semibold">
            選択中 · {item.kcode ?? "—"}
          </div>
          <h3 className="font-semibold mt-1">{shortName(item.itemName)}</h3>
        </div>
      </div>
      <div className="short-stay3-detail-grid grid grid-cols-1 xl:grid-cols-[minmax(0,3fr)_minmax(250px,1fr)] gap-4 mt-4">
        <div className="short-stay3-histograms grid grid-cols-1 lg:grid-cols-3 gap-3">
          <Histogram
            title="在院日数"
            bins={main}
            median={item.median}
            max={histogramMax}
          />
          <Histogram
            title="術前日数"
            bins={pre}
            median={quantile(
              rows.flatMap((r) =>
                r.preOpDays == null ? [] : Array(r.cases).fill(r.preOpDays),
              ),
              0.5,
            )}
            max={histogramMax}
          />
          <Histogram
            title="術後日数"
            bins={post}
            median={quantile(
              rows.flatMap((r) =>
                r.postOpDischargeDays == null
                  ? []
                  : Array(r.cases).fill(r.postOpDischargeDays),
              ),
              0.5,
            )}
            max={histogramMax}
          />
        </div>
        <div className="short-stay3-simulation border border-slate-200 rounded-lg p-3">
          <h4 className="text-xs font-semibold">
            在院日数変更による1日入院単価試算
          </h4>
          <table className="w-full text-xs mt-2">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="text-right p-1.5">在院日数</th>
                <th className="text-right p-1.5">1日入院単価</th>
              </tr>
            </thead>
            <tbody>
              {simulation.map((r) => (
                <tr
                  key={r.days}
                  className={`border-b ${r.current ? "bg-amber-100 font-semibold" : "border-slate-100"}`}
                >
                  <td className="p-1.5 text-right">
                    {r.days}日{r.current ? "（中央値）" : ""}
                  </td>
                  <td className="p-1.5 text-right">{yen(r.unit)}/日</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mt-4 border-t border-slate-200 pt-4">
        <h4 className="text-sm font-semibold">入院曜日 → 退院曜日</h4>
        <p className="mt-1 text-xs text-slate-500">
          選択期間に退院した症例を、入院から退院までの組み合わせで表示します。入院日は選択期間より前の場合があります。
        </p>
        <WeekdayHeatmap key={item.accountingCode} rows={rows} />
      </div>
    </section>
  );
}
function WeekdayHeatmap({ rows }: { rows: Hist[] }) {
  const [chosen, setChosen] = useState<{ admission: number; discharge: number } | null>(null);
  const counts = Array.from({ length: 7 }, () => Array(7).fill(0) as number[]);
  for (const row of rows) {
    if (row.admissionWeekday >= 1 && row.admissionWeekday <= 7 &&
        row.dischargeWeekday >= 1 && row.dischargeWeekday <= 7) {
      counts[row.admissionWeekday - 1][row.dischargeWeekday - 1] += row.cases;
    }
  }
  const admissionTotals = counts.map((row) => row.reduce((sum, n) => sum + n, 0));
  const dischargeTotals = WEEKDAYS.map((_, d) => counts.reduce((sum, row) => sum + row[d], 0));
  const total = admissionTotals.reduce((sum, n) => sum + n, 0);
  const max = Math.max(1, ...counts.flat());
  const strongest = counts.flatMap((row, a) => row.map((cases, d) => ({ admission: a, discharge: d, cases })))
    .sort((a, b) => b.cases - a.cases)[0];
  const active = chosen && counts[chosen.admission][chosen.discharge] > 0 ? chosen : strongest;
  const links = counts.flatMap((week, admission) => week.map((cases, discharge) => ({ admission, discharge, cases })))
    .filter((link) => link.cases > 0)
    .sort((a, b) => Number(a.admission === active.admission && a.discharge === active.discharge) -
      Number(b.admission === active.admission && b.discharge === active.discharge));
  return (
    <div className="mt-3 grid grid-cols-1 xl:grid-cols-[minmax(560px,1.35fr)_minmax(340px,1fr)] items-stretch gap-3">
      <div className="min-h-[410px] rounded-lg border border-slate-200 p-3 overflow-x-auto">
        <div className="flex justify-between gap-3 text-xs text-slate-500">
          <span>全{int(total)}件</span>
          <span>濃いほど件数が多い</span>
        </div>
        <table className="mt-2 w-full min-w-[560px] border-separate border-spacing-1 text-center text-xs tabular-nums">
          <thead>
            <tr>
              <th rowSpan={2} colSpan={2} aria-hidden="true" />
              <th colSpan={7} scope="colgroup" className="p-1 font-semibold text-slate-600">退院曜日</th>
              <th rowSpan={2} scope="col" className="p-1 text-slate-500">入院計</th>
            </tr>
            <tr>
              {WEEKDAYS.map((day) => <th key={day} scope="col" className="p-1 font-semibold">{day}</th>)}
            </tr>
          </thead>
          <tbody>
            {counts.map((week, a) => (
              <tr key={WEEKDAYS[a]}>
                {a === 0 && (
                  <th rowSpan={7} scope="rowgroup" className="w-7 bg-slate-50 text-slate-600 font-semibold">
                    <span className="inline-block tracking-widest"
                      style={{ writingMode: "vertical-rl", textOrientation: "upright" }}>入院曜日</span>
                  </th>
                )}
                <th scope="row" className="p-1 font-semibold">{WEEKDAYS[a]}</th>
                {week.map((cases, d) => {
                  const selected = active.admission === a && active.discharge === d && cases > 0;
                  const intensity = cases / max;
                  return (
                    <td key={WEEKDAYS[d]} className="p-0">
                      <button type="button" disabled={!cases}
                        onClick={() => setChosen({ admission: a, discharge: d })}
                        aria-label={`${WEEKDAYS[a]}曜入院から${WEEKDAYS[d]}曜退院、${cases}件`}
                        aria-pressed={selected}
                        title={`${WEEKDAYS[a]}→${WEEKDAYS[d]}: ${cases}件（${WEEKDAYS[a]}曜入院の${admissionTotals[a] ? Math.round(cases / admissionTotals[a] * 100) : 0}%）`}
                        className={`h-10 w-full rounded font-semibold transition ${selected ? "ring-2 ring-offset-1 ring-amber-500" : "hover:ring-2 hover:ring-sky-400"} disabled:cursor-default disabled:hover:ring-0`}
                        style={{ backgroundColor: cases ? `rgba(2, 132, 199, ${0.13 + intensity * 0.75})` : "#f8fafc",
                                 color: intensity > 0.55 ? "white" : "#0f172a" }}>
                        {cases || ""}
                      </button>
                    </td>
                  );
                })}
                <td className="p-1 font-semibold bg-slate-50">{admissionTotals[a]}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-semibold">
              <th colSpan={2} scope="row" className="p-1 text-slate-500">退院計</th>
              {dischargeTotals.map((n, d) => <td key={WEEKDAYS[d]} className="p-1 bg-slate-50">{n}</td>)}
              <td className="p-1 bg-slate-100">{total}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="min-h-[410px] rounded-lg border border-slate-200 p-3 flex flex-col">
        <div className="flex justify-between gap-3 text-xs text-slate-500">
          <span>〇 入院 → 〇 退院</span>
          <span>線の太さ＝件数</span>
        </div>
        <svg viewBox="0 0 420 340" className="mt-2 w-full min-h-[360px] flex-1" role="group"
          aria-label="入院曜日と退院曜日を件数に応じた太さの線で結んだ図">
          <text x="85" y="17" textAnchor="middle" className="fill-slate-500 text-xs">入院曜日</text>
          <text x="335" y="17" textAnchor="middle" className="fill-slate-500 text-xs">退院曜日</text>
          {links.map(({ admission, discharge, cases }) => {
            const selected = active.admission === admission && active.discharge === discharge;
            const label = `${WEEKDAYS[admission]}曜入院から${WEEKDAYS[discharge]}曜退院、${cases}件`;
            return (
              <g key={`${admission}-${discharge}`} role="button" tabIndex={0} aria-label={label}
                aria-pressed={selected}
                onClick={() => setChosen({ admission, discharge })}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setChosen({ admission, discharge });
                  }
                }} className="cursor-pointer">
                <title>{label}</title>
                <line x1="92" y1={52 + admission * 44} x2="328" y2={52 + discharge * 44}
                  stroke={selected ? "#f59e0b" : "#0284c7"} strokeOpacity={selected ? 1 : 0.35}
                  strokeWidth={2.5 + Math.sqrt(cases / max) * 10} strokeLinecap="round" />
                <line x1="92" y1={52 + admission * 44} x2="328" y2={52 + discharge * 44}
                  stroke="transparent" strokeWidth="20" />
              </g>
            );
          })}
          {WEEKDAYS.map((day, index) => (
            <g key={day}>
              <text x="60" y={56 + index * 44} textAnchor="end" className="fill-slate-700 text-xs font-semibold">
                {day}
              </text>
              <circle cx="85" cy={52 + index * 44} r="16"
                fill={active.admission === index ? "#f59e0b" : "#0284c7"} />
              <text x="85" y={56 + index * 44} textAnchor="middle" className="fill-white text-[10px] font-semibold tabular-nums">
                {admissionTotals[index]}
              </text>
              <circle cx="335" cy={52 + index * 44} r="16"
                fill={active.discharge === index ? "#f59e0b" : "#0284c7"} />
              <text x="335" y={56 + index * 44} textAnchor="middle" className="fill-white text-[10px] font-semibold tabular-nums">
                {dischargeTotals[index]}
              </text>
              <text x="360" y={56 + index * 44} textAnchor="start" className="fill-slate-700 text-xs font-semibold">
                {day}
              </text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}
function Histogram({
  title,
  bins,
  median,
  max,
}: {
  title: string;
  bins: { day: number; cases: number }[];
  median: number | null;
  max: number;
}) {
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <h4 className="text-xs font-semibold">
        {title}
        <span className="font-normal text-slate-500 ml-2">
          中央値 {days(median)}
        </span>
      </h4>
      <div className="mt-3 h-40 flex items-end gap-1 border-b border-slate-300">
        {bins.map((b) => (
          <div
            key={b.day}
            className="flex-1 h-full flex flex-col items-center justify-end"
          >
            <div className="text-[10px] font-semibold">{b.cases || ""}</div>
            <div
              className="w-full rounded-t bg-amber-500"
              style={{
                height: `${Math.max(b.cases ? 4 : 0, (b.cases / max) * 82)}%`,
              }}
            />
            <div className="text-[10px] pt-1">
              {b.day === 10 ? "10+" : b.day}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
